import React from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';
import Counter from './Counter/Counter';

export type PageCounterItem = {
  value: number;
  max?: number;
  decimals?: number;
  prefix?: LocalizedString;
  suffix?: LocalizedString;
  labelTop?: LocalizedString;
  labelBottom?: LocalizedString;
};

export type PageCounterTextBlock = {
  kind: 'text';
  text?: LocalizedString;
  align?: 'left' | 'center' | 'right';
  textColor?: string;
  fontSize?: number;
  fullWidth?: boolean;
};

export type PageCounterCounterBlock = {
  kind: 'counter';
} & PageCounterItem;

export type PageCounterBlock = PageCounterCounterBlock | PageCounterTextBlock;

export type PageCounterSectionConfig = {
  enabled?: boolean;
  topText?: LocalizedString;
  bottomText?: LocalizedString;
  type?: 'radial' | 'classic';
  variant?: 'plain' | 'cards';
  animate?: boolean;
  animationDurationMs?: number;
  animationDelayMs?: number;
  align?: 'left' | 'center' | 'right';
  perRow?: number;
  columns?: number;
  gap?: number;
  size?: number;
  textScale?: number;
  showPercent?: boolean;
  ringWidth?: number;
  shadow?: 'none' | 'soft' | 'medium' | 'strong' | 'glow';
  bgMode?: 'theme' | 'custom' | 'transparent';
  bgColor?: string;
  textColor?: string;
  ringBgColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  numberLocale?: string;
  blocks?: PageCounterBlock[];
  items?: PageCounterItem[];
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const safeColor = (v: any) => {
  const s = String(v || '').trim();
  if (!s) return '';
  return s.slice(0, 64);
};

const useInViewOnce = (rootMargin: string = '0px 0px -15% 0px') => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (inView) return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries && entries[0];
        if (e && e.isIntersecting) {
          setInView(true);
        }
      },
      { root: null, rootMargin, threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView, rootMargin]);

  return { ref, inView };
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const animateNumber = (from: number, to: number, durationMs: number, onUpdate: (v: number) => void, delayMs: number) => {
  let raf = 0;
  let start = 0;
  const d = Math.max(200, Math.min(8000, Number(durationMs) || 1200));
  const delay = Math.max(0, Math.min(5000, Number(delayMs) || 0));

  const tick = (ts: number) => {
    if (!start) start = ts;
    const elapsed = ts - start;
    const t = Math.max(0, Math.min(1, elapsed / d));
    onUpdate(lerp(from, to, easeOutCubic(t)));
    if (t < 1) raf = window.requestAnimationFrame(tick);
  };

  const timeout = window.setTimeout(() => {
    raf = window.requestAnimationFrame(tick);
  }, delay);

  return () => {
    try { window.clearTimeout(timeout); } catch {}
    try { window.cancelAnimationFrame(raf); } catch {}
  };
};

const PageCounterSection: React.FC<{ config?: PageCounterSectionConfig | null }> = ({ config }) => {
  const cfg: PageCounterSectionConfig = (config && typeof config === 'object') ? config : {};
  const { language } = useTranslation();
  const theme = useTheme();

  const enabled = cfg.enabled !== false;

  const type: 'radial' | 'classic' = cfg.type === 'classic' ? 'classic' : 'radial';
  const variant: 'plain' | 'cards' = cfg.variant === 'plain' ? 'plain' : 'cards';
  const perRow = Math.max(1, Math.min(6, Number(cfg.perRow ?? cfg.columns) || 3));
  const gap = clamp(cfg.gap, 8, 48) || 18;
  const size = clamp(cfg.size, 0.65, 1.6) || 1;
  const textScale = clamp(cfg.textScale, 0.75, 1.5) || 1;
  const showPercent = cfg.showPercent === true;
  const ringWidth = clamp(cfg.ringWidth, 4, 24) || 10;
  const shadow: any = cfg.shadow || (variant === 'cards' ? 'soft' : 'none');
  const numberLocale = String(cfg.numberLocale || '').trim() || undefined;

  const align: 'left' | 'center' | 'right' = cfg.align === 'left' ? 'left' : (cfg.align === 'right' ? 'right' : 'center');

  const animate = cfg.animate === true;
  const durationMs = Math.max(250, Math.min(8000, Number(cfg.animationDurationMs) || 1400));
  const delayMs = Math.max(0, Math.min(5000, Number(cfg.animationDelayMs) || 0));

  const topText = getLocalizedString(cfg.topText, language, 'de', '');
  const bottomText = getLocalizedString(cfg.bottomText, language, 'de', '');

  const bgMode: any = cfg.bgMode || 'theme';
  const bgColor = bgMode === 'custom' ? safeColor(cfg.bgColor) : (bgMode === 'transparent' ? 'transparent' : 'rgba(255,255,255,0.86)');
  const textColor = safeColor(cfg.textColor) || theme.palette.text.primary;
  const ringBgColor = safeColor(cfg.ringBgColor) || (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(17,24,39,0.12)');
  const gradientFrom = safeColor(cfg.gradientFrom) || theme.palette.primary.main;
  const gradientTo = safeColor(cfg.gradientTo) || theme.palette.secondary.main;

  const blocks: PageCounterBlock[] = Array.isArray(cfg.blocks)
    ? cfg.blocks
      .filter((x: any) => x && typeof x === 'object')
      .map((x: any) => {
        const kind = String(x?.kind || '').toLowerCase();
        if (kind === 'text') {
          return {
            kind: 'text',
            text: x?.text,
            align: x?.align,
            textColor: x?.textColor,
            fontSize: x?.fontSize,
            fullWidth: x?.fullWidth,
          } as PageCounterTextBlock;
        }
        return {
          kind: 'counter',
          value: Number(x?.value) || 0,
          max: x?.max == null ? undefined : (Number(x?.max) || 0),
          decimals: x?.decimals == null ? undefined : Math.max(0, Math.min(6, Number(x?.decimals) || 0)),
          prefix: x?.prefix,
          suffix: x?.suffix,
          labelTop: x?.labelTop,
          labelBottom: x?.labelBottom,
        } as PageCounterCounterBlock;
      })
    : (Array.isArray(cfg.items)
      ? cfg.items
        .filter((x: any) => x && typeof x === 'object')
        .map((x: any) => ({
          kind: 'counter',
          value: Number(x?.value) || 0,
          max: x?.max == null ? undefined : (Number(x?.max) || 0),
          decimals: x?.decimals == null ? undefined : Math.max(0, Math.min(6, Number(x?.decimals) || 0)),
          prefix: x?.prefix,
          suffix: x?.suffix,
          labelTop: x?.labelTop,
          labelBottom: x?.labelBottom,
        } as PageCounterCounterBlock))
      : []);

  const counterBlocks: PageCounterCounterBlock[] = blocks.filter((b: any) => b && typeof b === 'object' && (b as any).kind === 'counter') as any;

  const { ref, inView } = useInViewOnce();
  const [animatedValues, setAnimatedValues] = React.useState<number[]>(() => counterBlocks.map(() => 0));

  React.useEffect(() => {
    if (!enabled) return;
    setAnimatedValues(counterBlocks.map(() => 0));
  }, [counterBlocks.length, enabled]);

  React.useEffect(() => {
    if (!enabled) return;
    if (!animate) {
      setAnimatedValues(counterBlocks.map((it) => it.value));
      return;
    }
    if (!inView) return;
    const cleaners: Array<() => void> = [];
    setAnimatedValues(counterBlocks.map(() => 0));
    counterBlocks.forEach((it, idx) => {
      const stop = animateNumber(0, it.value, durationMs, (v) => {
        setAnimatedValues((prev) => {
          if (!Array.isArray(prev) || prev.length !== counterBlocks.length) {
            const next = counterBlocks.map((x) => x.value);
            next[idx] = v;
            return next;
          }
          const next = [...prev];
          next[idx] = v;
          return next;
        });
      }, delayMs + idx * 90);
      cleaners.push(stop);
    });
    return () => { cleaners.forEach((fn) => { try { fn(); } catch {} }); };
  }, [animate, delayMs, durationMs, enabled, inView, counterBlocks]);

  const cardSx = variant === 'plain'
    ? {}
    : {
      borderRadius: { xs: 4, md: 5 },
      border: '1px solid',
      borderColor: theme.palette.divider,
      bgcolor: theme.palette.background.paper,
      boxShadow: '0 18px 50px rgba(2,6,23,0.10)',
      px: { xs: 2, sm: 3 },
      py: { xs: 2.5, sm: 3 },
    };

  if (!enabled) return null;

  return (
    <Box ref={ref} sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        {(topText || bottomText) && (
          <Box sx={{ textAlign: 'center', mb: topText ? 4 : 3 }}>
            {topText ? (
              <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: -0.4 }}>{topText}</Typography>
            ) : null}
            {bottomText ? (
              <Typography variant="body1" color="text.secondary" sx={{ mt: topText ? 1 : 0 }}>{bottomText}</Typography>
            ) : null}
            {(topText || bottomText) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Box sx={{ width: 84, height: 4, borderRadius: 999, bgcolor: 'primary.main', opacity: 0.9 }} />
              </Box>
            ) : null}
          </Box>
        )}

        <Box sx={{ ...cardSx }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(2, perRow)}, minmax(0, 1fr))`, md: `repeat(${perRow}, minmax(0, 1fr))` },
              gap: `${gap}px`,
              alignItems: 'start',
              justifyItems: align === 'left' ? 'start' : (align === 'right' ? 'end' : 'center'),
            }}
          >
            {(() => {
              let counterIdx = 0;
              return blocks.map((b, idx) => {
                const kind = (b as any)?.kind;
                if (kind === 'text') {
                  const tb = b as PageCounterTextBlock;
                  const txt = getLocalizedString(tb.text, language, 'de', '');
                  if (!txt.trim()) return null;
                  const tAlign: 'left' | 'center' | 'right' = tb.align === 'left' ? 'left' : (tb.align === 'right' ? 'right' : align);
                  const tFontSize = clamp(tb.fontSize, 12, 32) || Math.round(18 * textScale);
                  const tColor = safeColor(tb.textColor) || textColor;
                  const fullWidth = tb.fullWidth !== false;
                  return (
                    <Box
                      key={`cntb-text-${idx}`}
                      sx={fullWidth ? { gridColumn: '1 / -1', width: '100%' } : {
                        minWidth: 0,
                        maxWidth: '100%',
                        width: tAlign === 'center' ? 'fit-content' : '100%',
                        display: 'grid',
                        placeItems: tAlign === 'left' ? 'start' : (tAlign === 'right' ? 'end' : 'center'),
                      }}
                    >
                      <Typography sx={{ textAlign: tAlign, fontSize: `${tFontSize}px`, lineHeight: 1.9, color: tColor, whiteSpace: 'pre-wrap' }}>
                        {txt}
                      </Typography>
                    </Box>
                  );
                }

                const it = b as PageCounterCounterBlock;
                const raw = animate ? (animatedValues[counterIdx] ?? 0) : it.value;
                const decimals = it.decimals == null ? 0 : it.decimals;
                const value = decimals > 0 ? Number(raw.toFixed(decimals)) : Math.round(raw);

                const prefix = getLocalizedString(it.prefix, language, 'de', '');
                const suffix = getLocalizedString(it.suffix, language, 'de', '');
                const labelTop = getLocalizedString(it.labelTop, language, 'de', '');
                const labelBottom = getLocalizedString(it.labelBottom, language, 'de', '');

                const max = (it.max != null && Number(it.max) > 0) ? Number(it.max) : undefined;
                const pct = max ? Math.max(0, Math.min(1, value / max)) : 1;

                const labelScale = textScale * size;

                const content = (
                  <Box sx={{ textAlign: align }}>
                    {labelTop ? (
                      <Typography variant="overline" sx={{ display: 'block', fontWeight: 900, letterSpacing: 1.1, opacity: 0.85, mb: 0.75, fontSize: `${0.72 * labelScale}rem` }}>
                        {labelTop}
                      </Typography>
                    ) : null}

                    <Counter
                      value={value}
                      prefix={prefix}
                      suffix={suffix}
                      bg={type === 'radial' ? ringBgColor : bgColor}
                      textColor={textColor}
                      radius={14}
                      scale={size}
                      variant={type === 'radial' ? 'radial' : 'classic'}
                      gradientFrom={gradientFrom}
                      gradientTo={gradientTo}
                      ringWidth={ringWidth}
                      shadow={shadow}
                      numberLocale={numberLocale}
                    />

                    {(showPercent && max) ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: `${0.78 * labelScale}rem` }}>
                        {Math.round(pct * 100)}%
                      </Typography>
                    ) : null}

                    {labelBottom ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, fontWeight: 700, lineHeight: 1.5, fontSize: `${0.95 * labelScale}rem` }}>
                        {labelBottom}
                      </Typography>
                    ) : null}
                  </Box>
                );

                const rendered = (
                  <Box
                    key={`cnt-${idx}`}
                    sx={{
                      minWidth: 0,
                      maxWidth: '100%',
                      width: align === 'center' ? 'fit-content' : '100%',
                      display: 'grid',
                      placeItems: align === 'left' ? 'start' : (align === 'right' ? 'end' : 'center'),
                    }}
                  >
                    {content}
                  </Box>
                );
                counterIdx += 1;
                return rendered;
              });
            })()}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PageCounterSection;
